// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Application composition root.
 *
 * Providers are assembled here and nowhere else, so a test can substitute the
 * API clients and a router without touching feature code
 * (`docs/05-CODING-STANDARDS.md` §10.3, `TST-042`).
 *
 * Query defaults encode two rules rather than leaving them to each call site:
 * a failed request with a definitive answer is not retried (`RUN-010`), and
 * results are considered stale quickly so an administrator is not shown a
 * long-cached view of a governed resource.
 */

import {
  ApiClientProvider,
  createAppTheme,
  SessionProvider,
  type ApiClients,
} from '@eai/ui-shared';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { AppLayout, SessionGate } from './app-shell';
import { AppRoutes } from './routes';

/** How long a fetched result is considered fresh, in milliseconds. */
export const DEFAULT_STALE_TIME_MS = 30_000;

/** Build the application query client. */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

/** Props for {@link App}. */
export interface AppProps {
  /** Injected API clients; production passes the HTTP-backed set. */
  readonly clients: ApiClients;
  /** Injected query client, used by tests to control caching. */
  readonly queryClient?: QueryClient;
  /** Router element, allowing a memory router in tests. */
  readonly router?: (children: ReactNode) => ReactNode;
}

/** Compose providers, chrome, and routes. */
export function App({ clients, queryClient, router }: AppProps): ReactNode {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const theme = useMemo(() => createAppTheme(prefersDark ? 'dark' : 'light'), [prefersDark]);
  const resolvedQueryClient = useMemo(() => queryClient ?? createAppQueryClient(), [queryClient]);
  const wrap = router ?? ((children: ReactNode) => <BrowserRouter>{children}</BrowserRouter>);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={resolvedQueryClient}>
        <ApiClientProvider clients={clients}>
          <SessionProvider>
            {wrap(
              // The gate sits inside the router so the sign-in screen and the
              // application share one history, and outside the layout so no
              // navigation renders before the server has said who is asking.
              <SessionGate>
                <AppLayout>
                  <AppRoutes />
                </AppLayout>
              </SessionGate>,
            )}
          </SessionProvider>
        </ApiClientProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
